(ns ductile.ui.components.combobox
  (:require [applied-science.js-interop :as j]
            [clojure.pprint :as pprint]
            [clojure.string :as str]
            [ductile.time.format :as time-format]
            [ductile.ui.components :as ui]
            [ductile.ui.icons :as icons]
            [ductile.ui.locale :refer [tr]]
            [ductile.ui.mobile :as mobile]
            [ductile.ui.scene :as scene]
            [ductile.ui.util :as util]
            [nextjournal.clerk.render.hooks :as hooks])
  (:require-macros [ductile.ui.scene.macros :refer [defscene]]))

(defn ->choice [value]
  {:ui/value value :ui/label (tr value)})

(defn boundary-code? [i]
  (and (> i 31)
       (< i 48)))

(defn match-codes? [c1 c2]
  (or (== c1 c2)
      (and (boundary-code? c1) (boundary-code? c2))))

(defn prev-index [i count]
  (if (zero? i) (dec count) (dec i)))

(defn next-index [i count]
  (if (= i (dec count)) 0 (inc i)))

(def START_WEIGHT 2.5)
(def BOUNDARY_WEIGHT 2)
(def CONSECUTIVE_WEIGHT 2.5)
(def EXACT_EXTRA 3)

(defn exact-score [needle haystack]
  (when-some [i (str/index-of haystack needle)]
    (let [exact-sum (+ EXACT_EXTRA (* CONSECUTIVE_WEIGHT (count needle)))]
      (if (zero? i)
        (+ exact-sum START_WEIGHT)
        (when (boundary-code? (j/call haystack :charCodeAt (dec i)))
          (+ BOUNDARY_WEIGHT exact-sum))))))

(defn match [{:keys [query text]}]
  (or (when-let [n (exact-score query text)]
        {:fuzzy/score n
         :fuzzy/chars (let [start (str/index-of text query)]
                        (when-not (neg? start)
                          (range start (+ start (count query)))))}) ;; if exact match exists, use its score
      (let [hlen (j/!get text :length)
            nlen (j/!get query :length)]
        (if (> nlen hlen)
          {:fuzzy/score 0}
          (loop [i 0
                 j 0
                 score 0
                 prev-matched? false
                 at-boundary? true
                 at-start? true
                 chars []]
            (cond (== i nlen) {:fuzzy/score score :fuzzy/chars chars}
                  (== j hlen) {:fuzzy/score score :fuzzy/chars chars}
                  :else
                  (let [nchar (j/call query :charCodeAt i)
                        hchar (j/call text :charCodeAt j)]
                    (if-let [match-weight (and (match-codes? nchar hchar)
                                               (cond at-start? START_WEIGHT
                                                     at-boundary? BOUNDARY_WEIGHT
                                                     prev-matched? CONSECUTIVE_WEIGHT
                                                     :else false))]
                      (recur (inc i)
                             (inc j)
                             (+ score match-weight)
                             true
                             (boundary-code? hchar)
                             false
                             (conj chars j))
                      (recur i
                             (inc j)
                             score
                             false
                             (boundary-code? hchar)
                             false
                             chars)))))))))

(defn extract-extra-keys [extra-keys choice]
  (map str/lower-case (vals (select-keys (:ui/value choice) extra-keys))))

(defn apply-query [{:keys [choices extra-keys query] :or {extra-keys []}}]
  (->> choices
       (keep (fn [choice]
               (let [{:as match :fuzzy/keys [score]}
                     (match {:query (str/lower-case query)
                             :text (str/join (concat [(str/lower-case (str (:ui/label choice)))]
                                                     (extract-extra-keys extra-keys choice)))})]
                 (when (pos? score)
                   (merge choice match)))))
       (sort-by :fuzzy/score)
       reverse))

(defn on-query-change [!state choices extra-keys event]
  (let [new-query (.. event -target -value)]
    (swap! !state assoc
           :query new-query
           :filtered-choices (apply-query {:choices choices
                                           :extra-keys extra-keys
                                           :query new-query})
           :selected-index 0
           :highlighted-index 0)))

(defn hide [state]
  (dissoc state :focused? :selected-index :highlighted-index :query :filtered-choices))

(defn- open-new-option-form! [!state handler opts]
  (let [query (:query @!state)
        args (if-let [k (:combobox/new-option-prefill-key opts)] {k query} {})
        on-pop (:combobox/new-option-on-pop opts)]
    (swap! !state hide)
    (ui/show-form-modal! handler args (when on-pop {:on-pop on-pop}))))

(defn on-input-action [!state on-change choices event & [{:combobox/keys [new-option-form-handler] :as opts}]]
  (case (.-code event)
    "ArrowUp"
    (swap! !state (fn [{:as state :keys [filtered-choices highlighted-index]}]
                    (assoc state :highlighted-index
                           (prev-index highlighted-index (count (or filtered-choices choices))))))
    "ArrowDown"
    (swap! !state (fn [{:as state :keys [filtered-choices highlighted-index]}]
                    (assoc state :highlighted-index
                           (next-index highlighted-index (count (or filtered-choices choices))))))
    "Enter"
    (let [visible (or (:filtered-choices @!state) choices)]
      (if (and (empty? visible) new-option-form-handler)
        (do (open-new-option-form! !state new-option-form-handler opts)
            (.. event -target blur)
            (.stopPropagation event))
        (do (swap! !state hide)
            (on-change (nth visible (:highlighted-index @!state)))
            (.. event -target blur)
            (.stopPropagation event))))
    "Escape"
    (do (swap! !state hide)
        (.. event -target blur)
        (.stopPropagation event))
    "Tab"
    (swap! !state hide)
    false))

(defn input [opts !state on-change choices extra-keys focused? highlighted-index value query]
  (let [on-input-change (fn on-change [event]
                          (on-query-change !state choices extra-keys event))
        on-key-down (fn on-key-down [event]
                      (on-input-action !state on-change choices event opts))
        on-focus (fn on-focus [event]
                   (let [index (util/index-of value choices)]
                     (swap! !state assoc
                            :focused? true
                            :highlighted-index index
                            :selected-index index)))
        input-opts (merge (dissoc opts :on-clear :clear-on-change? :ui/style)
                          {:variant :flex
                           :role "combobox"
                           :value (if focused?
                                    query
                                    (or (when-not (:clear-on-change? opts)
                                          (str (:ui/label value)))
                                        ""))
                           :placeholder (str (or (:placeholder opts)
                                                 (str (tr "Type to filter") "…")))
                           :on-change on-input-change
                           :on-key-down on-key-down
                           :on-focus on-focus
                           :right (when-not (:disabled? opts)
                                    [:div.flex.items-center.gap-2
                                     (:right opts)
                                     [:div.text-slate-500.cursor-pointer.hover:text-slate-700
                                      {:class ["right-2" "top-[10px]" "transition-all" (if focused? "rotate-180" "rotate-0")]}
                                      [ui/icon {:size 14} :chevron-down]]])})]
    [:div.combobox-input
     {:class (case (:width opts)
               :xs "w-14" ; 2 characters
               :sm "w-20" ; 4 to 5 characters
               :md "w-40" ; 8 to 12 characters
               :lg "w-56"
               :xl "w-96"
               :full "w-full"
               nil)}
     (case (:ui/style opts)
       :style/mobile [mobile/list-item-input {:input input-opts}]
       [ui/input input-opts])]))

(defn default-choice [{:ui/keys [label label-view description value]}]
  [:li.px-3.py-2
   (or label-view (str label))
   (when description
     [:div.text-xs.opacity-70 description])
   #_[:div.text-xs.font-mono (pr-str value)]])

(defn choices-list [{:as opts :keys [on-change data-testid] :combobox/keys [new-option-form-handler]} !state selected-index highlighted-index choice-view choices]
  (hooks/use-effect (fn on-choices-mount []
                      (let [on-hide (fn on-hide [event]
                                      (when-not (or (.closest (.-target event) ".combobox-choices")
                                                    (.closest (.-target event) ".combobox-input"))
                                        (swap! !state hide)))]
                        (js/document.addEventListener "click" on-hide)
                        (fn on-choices-unmount [] (js/document.removeEventListener "click" on-hide)))))
  (hooks/use-effect (fn []
                      (when-let [el (js/document.querySelector ".combobox-highlighted")]
                        (ui/scroll-into-view! ".combobox-highlighted" {:block "center"})))
                    [highlighted-index])

  (into [:ul.border.rounded.shadow-lg.w-lg.overflow-y-auto.bg-white.combobox-choices
         {:class ["min-w-[300px]" "max-h-[315px]" "left-0" "top-[100%]" "z-[1]"]
          :tab-index "-1"}
         (when (empty? choices)
           (if new-option-form-handler
             [:li.px-3.py-2.text-sm.cursor-pointer.text-indigo-600.hover:bg-indigo-50.flex.items-center.gap-1
              {:on-click (fn [e] (.stopPropagation e) (.preventDefault e)
                           (open-new-option-form! !state new-option-form-handler opts))}
              [ui/icon {:size 14} :plus] (tr "Create new")]
             [:li.px-3.py-2.text-sm.cursor-not-allowed.text-slate-400.select-none (tr "No results")]))]
        (map-indexed (fn [i choice]
                       [:li.border-b.last:border-b-0.cursor-pointer.select-none.text-sm
                        {:data-testid (or (:data-testid choice)
                                          (str "choice-" (:ui/label choice)))
                         :class (concat (if (= i selected-index)
                                          [(if (= i highlighted-index) "bg-indigo-700" "bg-indigo-600") "text-white"]
                                          ["hover:bg-indigo-100"])
                                        (when (= i highlighted-index)
                                          ["bg-indigo-100" "combobox-highlighted"]))
                         :on-click (fn [^js e]
                                     (.stopPropagation e)
                                     (.preventDefault e)
                                     (swap! !state hide)
                                     (on-change choice))
                         :aria-label (:ui/label choice)}
                        (if choice-view
                          [choice-view (:ui/value choice)]
                          [default-choice choice])]))
        choices))

(defn expand-value [choices value]
  (if (and (map? value) (contains? value :ui/value)) value
      (first (filter #(= (:ui/value %) value) choices))))

(defn desktop-view [{:as opts :keys [choices choice-view disabled? on-change on-clear extra-keys value clear-on-change?]}]
  (let [value (expand-value choices value)
        !ref (hooks/use-ref)
        !state (hooks/use-state {:selected-index (util/index-of value choices)})
        input-opts (cond-> (-> opts
                               (dissoc :choices :choice-view :on-change :extra-keys :value :ui/mobile? :on-clear)
                               (assoc :placeholder (or (and (map? (:placeholder opts)) (:ui/label (:placeholder opts)))
                                                       (:placeholder opts)
                                                       (:ui/label value))))
                     (and on-clear (not (nil? (:ui/value value))) (not disabled?))
                     (assoc :right [ui/clear-input {:on-click #(do (on-clear)
                                                                   (swap! !state dissoc :focused? :query))
                                                    :size (:size opts)}]))
        state @!state]
    [:div.relative.flex.items-center.gap-2
     [:div.relative.flex-auto {:ref !ref}
      [input input-opts !state on-change choices extra-keys (:focused? state) (:highlighted-index state) value (:query state "")]]
     (when (and (:focused? state) (not disabled?))
       [ui/dropdown-portal {:anchor !ref
                            :class "translate-y-[3px]"}
        [choices-list opts !state (:selected-index state) (:highlighted-index state) choice-view (:filtered-choices state choices)]])]))

(defn mobile-view [{:as opts :keys [value choices placeholder choice-view on-clear on-change disabled?]}]
  (let [value (expand-value choices value)
        !state (hooks/use-state {:query "" :highlighted-index 0})
        {:keys [query highlighted-index]} @!state
        limit 30
        filtered-choices (into []
                               (comp
                                (filter (fn [{:as choice :ui/keys [label]}]
                                          (if (str/blank? query)
                                            true
                                            (or (= value choice)
                                                (str/includes? (str/lower-case (str label)) (str/lower-case query)))))))
                               (sort-by (comp :entry/datetime :ui/value) choices))
        visible-choices (take limit filtered-choices)
        no-query-matches? (or (empty? visible-choices)
                              (and (not (str/blank? query))
                                   (every? #(= value %) visible-choices)))
        new-option-handler (when no-query-matches? (:combobox/new-option-form-handler opts))
        create-new-index (when new-option-handler (count visible-choices))
        navigable-count (cond-> (count visible-choices) new-option-handler inc)
        show-highlight? (not ui/is-mobile?)
        scroll-to-highlighted! #(js/requestAnimationFrame
                                 (fn [] (ui/scroll-into-view-with-margin! ".combobox-mobile-highlighted")))
        on-key-down (fn [^js event]
                      (case (.-code event)
                        "ArrowUp"
                        (when show-highlight?
                          (.preventDefault event)
                          (swap! !state update :highlighted-index prev-index navigable-count)
                          (scroll-to-highlighted!))
                        "ArrowDown"
                        (when show-highlight?
                          (.preventDefault event)
                          (swap! !state update :highlighted-index next-index navigable-count)
                          (scroll-to-highlighted!))
                        "Enter"
                        (cond
                          (and create-new-index
                               (or (not show-highlight?) (= highlighted-index create-new-index)))
                          (do (.stopPropagation event)
                              (open-new-option-form! !state (:combobox/new-option-form-handler opts) opts))

                          show-highlight?
                          (when-let [choice (nth visible-choices highlighted-index nil)]
                            (.stopPropagation event)
                            (on-change choice)))
                        nil))]
    [:<>
     [mobile/list-item-input {:icon :magnifying-glass
                              :input {:value query
                                      :disabled disabled?
                                      :on-change (fn [^js e]
                                                   (swap! !state assoc
                                                          :query (.. e -target -value)
                                                          :highlighted-index 0))
                                      :on-key-down on-key-down
                                      :on-clear #(do (when on-clear (on-clear))
                                                     (swap! !state assoc :query "" :highlighted-index 0))
                                      :placeholder (str placeholder)}}]
     (when-not disabled?
       [:<>
        (into [:<>]
              (map-indexed
               (fn [i {:as choice :ui/keys [label data-testid]}]
                 (let [selected? (= (:ui/value value) (:ui/value choice))
                       highlighted? (and show-highlight? (= i highlighted-index))]
                   [mobile/list-item-radio {:label (if choice-view [choice-view (:ui/value choice)] label)
                                            :class (when highlighted? ["bg-indigo-100" "combobox-mobile-highlighted"])
                                            :style {:scroll-margin "200px 0"}
                                            :on-mouse-enter #(when show-highlight?
                                                               (swap! !state assoc :highlighted-index i))
                                            :input {:on-change (fn on-change-choice-input []
                                                                 (on-change choice))
                                                    :checked selected?
                                                    :data-testid data-testid}}])))
              visible-choices)

        (when no-query-matches?
          (if-let [handler (:combobox/new-option-form-handler opts)]
            [mobile/list-item-disclose {:title [:div.flex.items-center.gap-1 [ui/icon {:size 14} :plus] (tr "Create new")]
                                        :class (when (and show-highlight? (= highlighted-index create-new-index))
                                                 ["bg-indigo-100" "combobox-mobile-highlighted"])
                                        :style {:scroll-margin "200px 0"}
                                        :on-mouse-enter #(when show-highlight?
                                                           (swap! !state assoc :highlighted-index create-new-index))
                                        :on-click #(open-new-option-form! !state handler opts)}]
            [:div.text-gray-400.text-center.p-4 (tr "No results")]))

        (when (< limit (count filtered-choices))
          [:div.text-gray-400.text-center.p-4 (tr "+%1 more results" (- (count filtered-choices) limit))])])]))

(defn view [opts]
  (if (:ui/mobile? opts)
    [mobile-view opts]
    [desktop-view opts]))

(defn carrier-choice [value]
  [:div.px-3.py-2
   [:div.text-sm.flex.items-center (:address/name value)
    (when-not (str/blank? (:carrier/code value))
      [:div.tabular-nums.opacity-80.ml-1 {:class "text-[11px]"} " (" (:carrier/code value) ")"])]
   [:div.opacity-70.leading-tight {:class ["text-[11px]"]}
    (when (:address/name value)
      [:div (:address/name value)])
    (when (:address/street value)
      [:div (:address/street value)])
    (when (or (:address/postcode value) (:address/city value))
      [:div (str/join " " [(:address/postcode value) (:address/city value)])])
    (when-let [country (:address/country value)]
      [:div (or (:country/code country) country)])]])

(defn transporter-choice [value]
  (let [address-name (get-in value [:transport/carrier :address/name])]
    [:div.px-3.py-2
     {:data-testid (str "truck-" (:truck/number value))}
     [:div.flex.justify-between.items-center
      [:div
       [:div.text-sm (:truck/number value)
        (when address-name (str " (" address-name ")"))]
       [:div.opacity-70.leading-tight {:class ["text-[11px]"]}
        [:div (str/join " / " (remove nil? [(:transport/license-plate value)
                                            (-> value :transport/driver :transport/driver-name)]))]]]
      [:div.text-black.size-5.text-slate-300 icons/truck]]]))

(defn mobile-transporter-choice [value]
  (let [address-name (get-in value [:transport/carrier :address/name])]
    [:div.px-3.py-2
     {:data-testid (str "truck-" (:truck/number value))}
     [:div
      [:div.text-sm (:truck/number value)]
      [:div.opacity-70.leading-tight {:class ["text-[11px]"]}
       [:div (tr :transport/license-plate) ": " (:transport/license-plate value)]
       [:div (tr :transport/carrier) ": " address-name]]]]))

(defn truck? [v]
  (contains? v :truck/number))

(defn transporter-or-carrier-choice [value]
  (if (truck? value)
    [transporter-choice value]
    [carrier-choice value]))

(defn transport-choice [value & [options]]
  (let [inset? (if (contains? options :ui/inset?)
                 (:ui/inset? options)
                 true)]
    [:div
     {:data-testid (str "transport-" (or (:transport/number value) (:transport/name value)))
      :class [(when inset? "px-3") "py-2"]}
     [:div
      [:div.text-sm.flex.items-center.gap-1
       (when-let [load-id (:load/id value)]
         [ui/badge {:variant :secondary} load-id])
       [:div.flex.items-center.gap-1.rounded
        {:class ["pl-1" "pr-2" "py-0.5" "bg-indigo-600" "text-white"]}
        (let [icon-opts {:size 18}]
          (case (:transport/mode value)
            :mode/truck [ui/icon icon-opts :truck]
            :mode/train [ui/icon icon-opts :train]
            :mode/ship [ui/icon icon-opts :ship]
            :mode/own-wheels [ui/icon icon-opts :car]
            [:<>]))
        [:span.font-bold (or (-> value :transport/transporter :truck/number)
                             (-> value :transport/carrier :address/name)
                             (str (:transport/number value) " " (:transport/name value)))]]
       (when-let [dest (:destination/location value)]
         (str " "
              (tr "to")
              " "
              (:address/postcode dest) " "
              (:address/city dest)))]
      [:div.text-slate-600.text-xs.mt-1.ml-1
       (when (or (:transport/license-plate value) (:transport/driver-name value))
         [:div.flex.gap-2
          (when (:transport/license-plate value)
            [:span (tr :transport/license-plate) ": " (:transport/license-plate value)])
          (when (:transport/driver-name value)
            [:span (tr :transport/driver-name) ": " (:transport/driver-name value)])])
       (when (= 1 (count (:transport/fahrzeuge value)))
         (let [fahrzeug (first (:transport/fahrzeuge value))]
           [:div.flex.gap-2
            (when-let [vin (:vehicle/vin fahrzeug)]
              [:span (tr :vehicle/vin) ": " [ui/vin vin]])
            (when-let [license-plate (:vehicle/license-plate fahrzeug)]
              [:span (tr :vehicle/license-plate) ": " license-plate])]))
       (when (:entry/datetime value)
         (str (tr "Entered at") ": "
              (time-format/->str {:format :datetime :style :short} (:entry/datetime value))))]]]))

(defn fahrzeug-choice [value & [options]]
  (let [inset? (if (contains? options :ui/inset?)
                 (:ui/inset? options)
                 true)]
    [:div
     {:data-testid (str "fahrzeug-" (:vehicle/vin value))
      :class [(when inset? "px-3") "py-2"]}
     [:div
      [:div.text-sm.flex.items-center.gap-2.flex-wrap
       [:div.flex.items-center.gap-1.rounded
        {:class ["pl-1" "pr-2" "py-0.5" "bg-indigo-600" "text-white"]}
        [ui/icon {:size 18} :car]
        [ui/vin (:vehicle/vin value)]]
       (when (:vehicle/license-plate value)
         [:span.tabular-nums.font-bold.text-xs.text-slate-800 (:vehicle/license-plate value)])

       [:span.text-xs.text-slate-600
        (->> [(:contract/name value)
              (str (get-in value [:exit/transport-order :destination/location :address/postcode])
                   " "
                   (get-in value [:exit/transport-order :destination/location :address/city]))]
             (remove str/blank?)
             (str/join " → "))]]
      [:div.text-slate-600.text-xs.mt-1
       [:div (when (:entry/datetime value)
               (str (tr "Entered at") ": "
                    (time-format/->str {:format :datetime :style :short} (:entry/datetime value))))]
       [:div (when (:exit/datetime value)
               (str (tr "Exited at") ": "
                    (time-format/->str {:format :datetime :style :short} (:exit/datetime value))))]]]]))

(def demo-fahrzeug
  {:contract/name "Kia Distribuzione"
   :make/name "Kia"
   :vehicle/model "Sportage (NQ5)"
   :color/description "(USG) YUCCA STEEL GREY M"
   :color/gradient-from "#ff1933"
   :color/gradient-to "#090912"
   :vehicle/vin "U5YPU81BASL375939"
   :vehicle/license-plate "M-U39JT14"
   :fahrzeug/parking-spot {:parking-spot/zone "Quarantäne SP3"
                           :parking-spot/row "1"
                           :parking-spot/place "2"}
   :service.event/completed #{{:service/code "Paket Aldi"}}
   :entry/datetime #inst "2025-04-17T14:42:00"
   :exit/datetime #inst "2025-04-19T19:09:00"
   :exit/transport-order {:destination/location
                          {:location/longitude 16.781033
                           :external.kia/location-codes ["07316"]
                           :address/country #:country{:code "ITA"}
                           :location/types [:location.type/dealer]
                           :address/province-code "BA"
                           :ductile/id #uuid "4cd08698-3bd1-4840-a462-1c1d1a010056"
                           :address/postcode "70026"
                           :address/street "SS 96 km 119400 Cda Lamarossa"
                           :address/region "Puglia"
                           :address/name "EUROMOTOR AUTOMOBILI S.r.l."
                           :address/city "MODUGNO"
                           :location/latitude 41.082865}}})

(defscene fahrzeug-choice-card
  :params (atom {:selected-choice {:ui/value demo-fahrzeug}})
  [!state]
  [fahrzeug-choice
   !state
   {:ui/value demo-fahrzeug}
   {:inset? false?}])

(defscene combobox ; FIXME dropdown portal outside iframe
  :params (atom {:choices
                 (map
                  (fn [carrier]
                    {:ui/label (:address/name carrier)
                     :ui/value carrier})
                  [{:address/city "Regensburg",
                    :address/name "ARS Altmann AG Regensburg\nAutomobillogistik",
                    :address/postcode "93055",
                    :address/street "Wiener Straße 25",
                    :ductile/id #uuid "c7b1d349-b086-4e19-82ba-dcd0445e8fdd"}
                   {:address/city "Eitting",
                    :address/name "ARS Altmann AG Eitting",
                    :address/postcode "85462",
                    :address/street "Oberfeld 6",
                    :ductile/id #uuid "d90de21c-da5f-4f1b-bf78-76666ad7a7a1"}
                   {:address/city "Salzgitter",
                    :address/name "Cevik Mobility GmbH",
                    :address/postcode "38239",
                    :address/street "Am Ehrenmal 7a",
                    :ductile/id #uuid "7645b79a-1f33-4441-8ae2-5d04a952e7e3"}
                   {:address/city "Riedstadt",
                    :address/name "calogs GmbH",
                    :address/postcode "64560",
                    :address/street "Bertha-von-Suttner-Str. 3",
                    :ductile/id #uuid "645f3c55-a853-499a-aa51-de9c3be0b53f"}
                   {:address/city "Veitshöchheim",
                    :address/name "Durnev Sergej n.e.k.",
                    :address/postcode "97209",
                    :address/street "Stresemannstr. 1",
                    :ductile/id #uuid "3977335c-5662-4c6f-b45b-f567791e3d52"}
                   {:address/city "Michelsneukirchen",
                    :address/name "Stigler Franz GmbH",
                    :address/postcode "93185",
                    :address/street "Buchenweg 4",
                    :ductile/id #uuid "d6b36062-ce73-458a-8a46-9d8fc18114e8"}
                   {:address/city "Michelsneukirchen",
                    :address/name "ECO StARS GmbH",
                    :address/postcode "93185",
                    :address/street "Buchenweg 4",
                    :ductile/id #uuid "5dd12777-2093-4561-90ce-bcd0954e14db"}
                   {:address/city "Michelsneukirchen",
                    :address/name "BFST Transport GmbH",
                    :address/postcode "93185",
                    :address/street "Buchenweg 4",
                    :ductile/id #uuid "c5321044-827a-458b-8923-7afb619092ec"}
                   {:address/city "Rüsselsheim am Main",
                    :address/name "Savvidis Athanasios n.e.k.",
                    :address/postcode "65428",
                    :address/street "Taunusstr. 45",
                    :ductile/id #uuid "0ebba6fa-257a-4050-bcc3-760b5b1bb765"}
                   {:address/city "Garbsen",
                    :address/name "Birol Sokur n.e.k.",
                    :address/postcode "30827",
                    :address/street "Liebermannstraße 6",
                    :ductile/id #uuid "56575bc2-44ae-4338-ac8a-fcc370e2d795"}
                   {:address/city "Wolnzach",
                    :address/name "AFT H. Altmann Fahrzeugtransporte",
                    :address/postcode "85283",
                    :address/street "Feierabendmühle 2",
                    :ductile/id #uuid "6e1fb280-3183-4c40-baaa-b2d74b437663"}
                   {:address/city "Zwickau",
                    :address/name "Eric Mahler Autotransporte",
                    :address/postcode "08056",
                    :address/street "Obere Kohlenstraße 18",
                    :ductile/id #uuid "48982059-cfdf-4860-b3bb-eb6c050b5415"}])})
  [!state]
  [:div.flex.flex-col.gap-3
   [:div {:class ["max-w-[300px]"]}
    [view {:choices (:choices @!state)
           :choice-view carrier-choice
           :value (:value @!state)
           :on-change (fn [value]
                        (swap! !state assoc :value value))
           :extra-keys [:address/name]}]]
   [:div.ml-2.text-xs
    [:div.font-bold.mb-1 "Selected value:"]
    (if-let [value (:value @!state)]
      [:pre.font-mono
       (with-out-str (pprint/pprint value))]
      "nothing selected")]])
